import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {hasOneliner} from '../../utils';
import './style.scss';

/**
 * @class
 * @name Oneliner
 * @extends Component
 * @param {Object} user - all the user data
 * @param {Boolean} allowEditing - whether the user can edit
 * @param {String} authToken - Fiverr authentication token
 * Returns the markup of the user oneliner & ratings
 */

class Oneliner extends Component {

    constructor(props) {
        super(props);

        this.state = {
            mode: 'view', // view, edit, error
            oneliner: '',
            initialOneliner: '',
            errorText: ''
        };

        this.onEditIconClick = this.onEditIconClick.bind(this);
        this.onSaveClick = this.onSaveClick.bind(this);
        this.onCancelClick = this.onCancelClick.bind(this);
        this.onInputChange = this.onInputChange.bind(this);
        this.onInputKeyPress = this.onInputKeyPress.bind(this);
    }

    componentWillMount() {

        let onelinerText = '';

        if (hasOneliner(this.props.user.one_liner)) {
            onelinerText = this.props.user.one_liner;
        } else if (this.props.allowEditing) {
            onelinerText = i18n.t('seller_card.oneliner.placeholder');
        }

        this.setState({
            oneliner: onelinerText,
            initialOneliner: onelinerText
        });

    }

    onEditIconClick() {
        this.setState({
            mode: 'edit'
        });
    }

    onSaveClick(e) {
        e.preventDefault();

        const check = this.checkForValid();
        if (check.isValid) {

            if ((typeof fetch !== 'undefined') && (this.state.oneliner !== this.state.initialOneliner)) {

                fetch(`/users/${this.props.user.username}`, {
                    method: 'PATCH',
                    body: encodeURI(`user[profile_attributes][one_liner]=${this.state.oneliner}`),
                    headers: new Headers({
                        'Content-Type':'application/x-www-form-urlencoded',
                        'X-CSRF-Token': this.props.authToken
                    }),
                    credentials: 'same-origin'
                })
                    .catch((err) => {
                        console.log('There was an error when trying to update the oneliner: ', err.message); // eslint-disable-line
                    });

            }

            this.setState({
                mode: 'view',
                initialOneliner: this.state.oneliner,
                errorText: ''
            });

        } else {

            this.setState({
                mode: 'error',
                errorText: check.errorText
            });

        }
    }

    onCancelClick(e) {
        e.preventDefault();
        this.setState({
            mode: 'view',
            oneliner: this.state.initialOneliner,
            errorText: ''
        });
    }

    onInputChange(e) {
        this.setState({
            oneliner: e.target.value,
            errorText: ''
        });
    }

    onInputKeyPress(e) {
        if (e.key === 'Enter') {
            this.onSaveClick(e);
        }
    }

    checkForValid() {

        const value = this.state.oneliner;

        let isValid = true,
            errorText = '';

        // validate oneliner
        if ((value === '') || (value.length < 3)) {
            // blank text, text too short
            isValid = false;
            errorText = i18n.t('seller_card.oneliner.error_oneliner_short');
        } else if (value.length > 70) {
            // text too long
            isValid = false;
            errorText = i18n.t('seller_card.oneliner.error_oneliner_long');
        } else if (value === this.state.initialOneliner) {
            // text hasn't changed, do nothing
        } else if (value.trim() === i18n.t('seller_card.oneliner.placeholder')) {
            // text is the default placeholder, do nothing
        } else {
            if (/^[a-zA-Z0-9\s\,\?\:\'\"\!]+$/.test(value)) { // eslint-disable-line
                // text has changed & is valid
            } else {
                // text has changed, but is invalid
                isValid = false;
                errorText = i18n.t('seller_card.oneliner.error_oneliner_invalid');
            }
        }

        return {
            isValid,
            errorText
        };

    }

    renderEditIcon() {
        return (
            <i className="fa fa-pencil icn-edit" onClick={this.onEditIconClick}></i>
        );
    }

    renderEditForm() {

        const showError = (this.state.mode === 'error' && this.state.errorText !== '');

        return (
            <form>
                <input type="text" name="user[profile_attributes][one_liner]" autoFocus="autofocus" maxLength="70" ref={(inp) => inp && inp.focus()} value={this.state.oneliner} autoComplete="off" onChange={(e) => this.onInputChange(e)} onKeyPress={(e) => this.onInputKeyPress(e)}/>
                <button className="btn-lrg-standard btn-white m-r-20" onClick={this.onCancelClick}>{i18n.t('general.cancel')}</button>
                <button className="btn-lrg-standard" onClick={this.onSaveClick}>{i18n.t('general.update')}</button>
                {showError &&
                    <div className="error-container">{this.state.errorText}</div>
                }
            </form>
        );

    }

    render() {

        const {
            user,
            allowEditing
        } = this.props;

        const showOneliner = hasOneliner(user.one_liner) || allowEditing,
            userRatingsCount = parseInt(user.ratings_count, 10),
            showRating = user.is_seller && user.ratings_count > 0,
            showError = this.state.mode === 'error' && this.state.errorText !== '',
            showEdit = this.state.mode === 'edit' || this.state.mode === 'error';

        return (

            <div className={`oneliner-wrapper${showEdit ? ' edit-mode' : ''}${showError ? ' error-mode' : ''}${!showRating ? ' no-rating' : ''}`}>

                {showOneliner &&
                    <small className="oneliner">
                        {this.state.oneliner}
                    </small>
                }

                {allowEditing && this.renderEditIcon()}
                {showEdit && this.renderEditForm()}

                {showRating &&
                    <div className="ratings-wrapper">
                        <span className={`star-rating-s15 rate-${Math.round(user.rating * 2)}`} data-user-rating={user.rating} data-user-ratings-count={user.ratings_count}></span>
                        <p className="rating-text">
                            <strong>{user.rating.toFixed(1)}</strong> {`(${i18n.t('seller_card.num_reviews', { count: userRatingsCount < 1000 ? userRatingsCount : '1k+'})})`}
                        </p>
                    </div>
                }

            </div>
        );

    }

}

Oneliner.propTypes = {
    user: PropTypes.object.isRequired,
    allowEditing: PropTypes.bool.isRequired,
    authToken: PropTypes.string.isRequired
};

export default Oneliner;


// WEBPACK FOOTER //
// ./src/components/oneliner/index.js